import { Injectable } from '@angular/core';
import { Institution } from '../../Services/Institution/Models/institution.model';
import { User } from '../../Services/User/Models/User.model';
import { SortDirection } from '../SortService/sort.service';

/**
 * State structure for institution search
 */
export interface InstitutionSearchState {
  searchTerm: string;
  results: Institution[];
  sortConfig: {
    column: string;
    direction: SortDirection;
  };
}

/**
 * State structure for user search
 */
export interface UserSearchState {
  searchTerm: string;
  searchType: 'name' | 'email';
  results: User[];
  sortConfig: {
    column: string;
    direction: SortDirection;
  };
  hasOverflow?: boolean;
}

/**
 * Service to persist search state across navigation.
 * State is maintained in memory and cleared when navigating via primary sidebar navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchStateService {
  private institutionState: InstitutionSearchState | null = null;
  private userState: UserSearchState | null = null;

  // ============ Institution Search State ============

  /**
   * Save institution search state
   */
  saveInstitutionState(state: InstitutionSearchState): void {
    this.institutionState = { ...state };
  }

  /**
   * Get cached institution search state
   */
  getInstitutionState(): InstitutionSearchState | null {
    return this.institutionState;
  }

  /**
   * Check if there are cached institution search results
   */
  hasInstitutionResults(): boolean {
    return this.institutionState !== null && this.institutionState.results.length > 0;
  }

  /**
   * Clear institution search state
   */
  clearInstitutionState(): void {
    this.institutionState = null;
  }

  // ============ User Search State ============

  /**
   * Save user search state
   */
  saveUserState(state: UserSearchState): void {
    this.userState = { ...state };
  }

  /**
   * Get cached user search state
   */
  getUserState(): UserSearchState | null {
    return this.userState;
  }

  /**
   * Check if there are cached user search results
   */
  hasUserResults(): boolean {
    return this.userState !== null && this.userState.results.length > 0;
  }

  /**
   * Clear user search state
   */
  clearUserState(): void {
    this.userState = null;
  }

  // ============ Global State Management ============

  /**
   * Clear all search states (called by sidebar navigation)
   */
  clearAll(): void {
    this.institutionState = null;
    this.userState = null;
  }
}
